import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from "react-i18next";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import { useLocalFilters } from '../../../hooks/useLocalFilters';
import { useOcelMetadata } from '../../../stores/OcelContext';
import TileSettingsModal from '../../../components/common/TileSettingsModal';
import LocalFiltersActiveBadge from '../../../components/common/LocalFiltersActiveBadge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../../components/ui/tooltip';
import { createFilteredRequest, makePostRequest } from '../../../services/apiClient';
import LiefertermintreueKPISettings from './LiefertermintreueKPISettings';
import { useObjectTypeActivities } from './hooks/useObjectTypeActivities';
import { useObjectDateAttributes } from './hooks/useObjectDateAttributes';
import {
  ALL_TIME_PRESET,
  resolveCurrentRange,
  resolvePreviousRange,
  formatRangeLabel,
  formatComparisonCaption,
  detectPeriodPresetFromDateRange,
} from '../_shared/periodPresets';
import {
  KPIValueRow,
  KPIValueColumn,
  KPIValueSlot,
  KPITrendSlot,
  KPIBigValue,
  KPICaption,
  KPITrendBadge,
} from '../_shared/kpiTrendDisplay';

const EMPTY_TILE_FILTER = { enabled: false, mode: 'include', matchType: 'any', conditions: [] };

// Trend indicator is absolute percentage points (aktueller Wert - Vorperiodenwert),
// compliance_rate is already 0-100, so the raw difference already is "pp".
const formatTrendPP = (trend) => {
  const sign = trend.diff > 0 ? '+' : trend.diff < 0 ? '−' : '';
  return `${sign}${Math.abs(trend.diff).toFixed(1)} pp`;
};

const LiefertermintreueKPI = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    globalFilterState,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const metadata = useOcelMetadata(activeFile);

  const availableActivities = useMemo(
    () => (metadata?.activity_types ? Object.keys(metadata.activity_types).sort() : []),
    [metadata]
  );

  // KPI data + UI state
  const [data, setData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [resolvedRange, setResolvedRange] = useState(null);
  const [resolvedPreviousRange, setResolvedPreviousRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);
  const isInitialMount = useRef(true);

  // Form state
  const [objectType, setObjectType] = useState('');
  const [refDateType, setRefDateType] = useState('object_attribute');
  const [refDateValue, setRefDateValue] = useState('');
  const [actualDateType, setActualDateType] = useState('object_attribute');
  const [actualDateValue, setActualDateValue] = useState([]);
  const [kpiTitle, setKpiTitle] = useState('Delivery Date Compliance');
  const [tileEventAttributeFilter, setTileEventAttributeFilter] = useState(EMPTY_TILE_FILTER);

  // Betrachtungszeitraum / Vorperioden-Vergleich configuration
  const [periodPreset, setPeriodPreset] = useState(ALL_TIME_PRESET.key);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showPeriodLabel, setShowPeriodLabel] = useState(true);
  const [showExtraStats, setShowExtraStats] = useState(true);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [trendDirection, setTrendDirection] = useState('higherIsBetter');

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const objectTypes = useMemo(
    () => (metadata?.object_types ? Object.keys(metadata.object_types) : []),
    [metadata],
  );

  const { activities: activitiesForType, loading: activitiesLoading } = useObjectTypeActivities(activeFile, objectType);
  const { dateAttributes: dateAttrsForType, loading: dateAttrsLoading } = useObjectDateAttributes(activeFile, objectType);

  // Restore from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.refDateType !== undefined) setRefDateType(initialSettings.refDateType);
    if (initialSettings.refDateValue !== undefined) setRefDateValue(initialSettings.refDateValue);
    if (initialSettings.actualDateType !== undefined) setActualDateType(initialSettings.actualDateType);
    if (initialSettings.actualDateValue !== undefined) {
      const v = initialSettings.actualDateValue;
      setActualDateValue(Array.isArray(v) ? v : v ? [v] : []);
    }
    if (initialSettings.kpiTitle !== undefined) setKpiTitle(initialSettings.kpiTitle);
    if (initialSettings.tileEventAttributeFilter !== undefined) setTileEventAttributeFilter(initialSettings.tileEventAttributeFilter);
    if (initialSettings.periodPreset !== undefined) {
      setPeriodPreset(initialSettings.periodPreset);
    } else {
      // Configs saved before periodPreset existed — derive a best-guess preset
      // from the legacy resolved dateRange so old tiles keep working unchanged.
      setPeriodPreset(detectPeriodPresetFromDateRange(initialSettings.localFilters?.dateRange));
    }
    if (initialSettings.customStart !== undefined) setCustomStart(initialSettings.customStart);
    if (initialSettings.customEnd !== undefined) setCustomEnd(initialSettings.customEnd);
    if (initialSettings.showPeriodLabel !== undefined) setShowPeriodLabel(initialSettings.showPeriodLabel);
    if (initialSettings.showExtraStats !== undefined) setShowExtraStats(initialSettings.showExtraStats);
    if (initialSettings.comparisonEnabled !== undefined) setComparisonEnabled(initialSettings.comparisonEnabled);
    if (initialSettings.trendDirection !== undefined) setTrendDirection(initialSettings.trendDirection);
    setPendingApply(true);
  }, []);

  // Register settings getter
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      objectType,
      refDateType,
      refDateValue,
      actualDateType,
      actualDateValue,
      kpiTitle,
      tileEventAttributeFilter,
      localFilters,
      periodPreset, customStart, customEnd, showPeriodLabel, showExtraStats, comparisonEnabled, trendDirection,
    }));
  }, [
    objectType, refDateType, refDateValue, actualDateType, actualDateValue, kpiTitle, tileEventAttributeFilter, localFilters,
    periodPreset, customStart, customEnd, showPeriodLabel, showExtraStats, comparisonEnabled, trendDirection,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved kpiTitle.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(kpiTitle || "Delivery Date Compliance");
  }, [kpiTitle]);

  const handleRefDateTypeChange = useCallback((val) => {
    setRefDateType(val);
    setRefDateValue('');
  }, []);

  const handleActualDateTypeChange = useCallback((val) => {
    setActualDateType(val);
    setActualDateValue([]);
  }, []);

  const handleObjectTypeChange = useCallback((val) => {
    setObjectType(val);
    setRefDateValue('');
    setActualDateValue([]);
  }, []);

  const handleOpenSettings = useCallback((e) => {
    e.stopPropagation();
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback((val) => {
    setIsSettingsModalOpen(val);
    if (!val) cancelDraftFilters();
  }, [cancelDraftFilters]);

  const isFormValid = useCallback(() => {
    return !!(objectType && refDateValue && actualDateValue.length > 0 && kpiTitle);
  }, [objectType, refDateValue, actualDateValue, kpiTitle]);

  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchKPIData = useCallback(async () => {
    if (!activeFile) {
      setError('No active file selected.');
      return;
    }
    if (!isFormValid()) {
      if (hasFetched) setError('Please fill in all required fields.');
      return;
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      const now = new Date();
      const currentRange = resolveCurrentRange(periodPreset, { customStart, customEnd }, now);
      const previousRange = comparisonEnabled
        ? resolvePreviousRange(periodPreset, currentRange, now)
        : null;

      // A non-"all" period is itself a local override, same as any other
      // local filter — decide local-vs-global the same way useLocalFilters
      // does, but without depending on this render's (possibly stale) value.
      const usesLocalFilters = hasLocalFilters || periodPreset !== ALL_TIME_PRESET.key;
      const filterBase = usesLocalFilters ? localFilters : globalFilterState;

      const baseRequest = {
        file_type: activeFile,
        object_type: objectType,
        ref_date_type: refDateType,
        ref_date_value: refDateValue,
        actual_date_type: actualDateType,
        actual_date_value: actualDateValue,
        kpi_title: kpiTitle,
        tileEventAttributeFilter: tileEventAttributeFilter.enabled ? tileEventAttributeFilter : undefined,
      };

      const currentRequest = createFilteredRequest(baseRequest, { ...filterBase, dateRange: currentRange });
      const fetches = [makePostRequest('/kpi/delivery-date-compliance', currentRequest, {}, 'Delivery Date Compliance KPI')];

      if (previousRange) {
        const previousRequest = createFilteredRequest(baseRequest, { ...filterBase, dateRange: previousRange });
        fetches.push(makePostRequest('/kpi/delivery-date-compliance', previousRequest, {}, 'Delivery Date Compliance KPI (Vorperiode)'));
      } else {
        fetches.push(Promise.resolve(null));
      }

      const [currentResponse, previousResponse] = await Promise.all(fetches);
      if (seq !== fetchSeqRef.current) return;
      setData(currentResponse);
      setPreviousData(previousResponse);
      setResolvedRange(currentRange);
      setResolvedPreviousRange(previousRange);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err.message || 'An unknown error occurred.');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [
    activeFile, objectType, refDateType, refDateValue, actualDateType, actualDateValue, kpiTitle, tileEventAttributeFilter,
    hasFetched, isFormValid, periodPreset, customStart, customEnd, comparisonEnabled, hasLocalFilters, localFilters, globalFilterState,
  ]);

  // Auto-reload on filter / activeFile change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    if (hasFetched && !loading) fetchKPIData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  // Auto-load after defaults restored
  useEffect(() => {
    if (!isInitialMount.current && hasFetched && !loading && !data) {
      fetchKPIData();
    }
  }, [hasFetched, loading, data, fetchKPIData]);

  // Mark initial mount done
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  const handleApplySettings = useCallback(() => {
    applyDraftFilters();
    setPendingApply(true);
    if (onTitleChange && kpiTitle) onTitleChange(kpiTitle);
    setIsSettingsModalOpen(false);
  }, [applyDraftFilters, onTitleChange, kpiTitle]);

  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchKPIData();
  }, [pendingApply, fetchKPIData]);

  // Expose refresh + header actions to grid parent
  useEffect(() => {
    if (onRefresh) onRefresh(fetchKPIData);
  }, [onRefresh, fetchKPIData]);

  // A non-"all" period is a local override in its own right, but it no longer
  // lives inside localFilters.dateRange (see fetchKPIData) — reflect it in
  // the badge here instead of writing it back into hook state.
  const periodIsLocalOverride = periodPreset !== ALL_TIME_PRESET.key;
  const displayHasLocalFilters = hasLocalFilters || periodIsLocalOverride;
  const displayLocalFilterCount = localFilterCount + (periodIsLocalOverride ? 1 : 0);

  const headerActions = useMemo(() => (
    <div className="flex items-center gap-2">
      {displayHasLocalFilters && (
        <LocalFiltersActiveBadge
          hasLocalFilters={displayHasLocalFilters}
          localFilterCount={displayLocalFilterCount}
          onClearLocalFilters={clearLocalFilters}
        />
      )}
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Open KPI settings"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    </div>
  ), [handleOpenSettings, displayHasLocalFilters, displayLocalFilterCount, clearLocalFilters]);

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // ── Derived display values ─────────────────────────────────────────────────
  const rate = data?.compliance_rate;
  const isNumeric = rate !== null && rate !== undefined;

  // "Gesamter Zeitraum" has no meaningful Vorperiode at all — treat comparison
  // as off regardless of the raw stored flag (mirrors Aggregation/Efficiency KPI).
  const effectiveComparisonEnabled = comparisonEnabled && periodPreset !== ALL_TIME_PRESET.key;

  const previousRate = previousData?.compliance_rate;
  const hasPreviousValue = effectiveComparisonEnabled && previousRate !== null && previousRate !== undefined;

  const periodLabel = showPeriodLabel && resolvedRange
    ? formatRangeLabel(resolvedRange)
    : null;

  const previousPeriodLabel = resolvedPreviousRange
    ? formatRangeLabel(resolvedPreviousRange)
    : null;

  const comparisonCaption = previousPeriodLabel
    ? formatComparisonCaption(periodPreset, previousPeriodLabel)
    : null;

  const trend = useMemo(() => {
    if (!effectiveComparisonEnabled || !isNumeric || !hasPreviousValue) return null;
    const diff = rate - previousRate;
    const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
    const improved = trendDirection === 'higherIsBetter' ? diff > 0 : diff < 0;
    const worsened = trendDirection === 'higherIsBetter' ? diff < 0 : diff > 0;
    const color = diff === 0 ? 'neutral' : improved ? 'positive' : worsened ? 'negative' : 'neutral';
    return { diff, direction, color };
  }, [effectiveComparisonEnabled, isNumeric, hasPreviousValue, rate, previousRate, trendDirection]);

  const renderKPICard = () => {
    if (!data) return null;
    return (
      <div className="flex flex-col gap-4 w-full">
        <TooltipProvider>
          {/* w-full is forced explicitly (not left to flex-stretch) so this block's
              footprint can never change with the comparison state below — it stays
              exactly as wide as before regardless, which can't affect the
              On Time/Late/Evaluated row's own grid-cols-3 sizing. */}
          <div className="w-full min-w-0 flex flex-col items-center gap-1">
            <div className="text-sm font-medium text-gray-700">
              {data.kpi_title}
            </div>

            {/* KPIValueRow is a CSS container-query container ([container-type:inline-size]),
                so the clamp() sizes inside its columns resolve against ITS OWN box width —
                not the viewport — scaling down smoothly as the row narrows (browser zoom
                shrinks the tile's CSS-pixel width while fixed rem sizes don't).

                Deliberately flex-nowrap, NOT flex-wrap: an earlier version wrapped the
                trend column below the value column once it ran out of horizontal room.
                That added a whole extra line, which could make the card taller than
                the grid tile's fixed pixel height (react-grid-layout row height doesn't
                grow with content) and push the *outer* GridComponent into its
                overflow-auto scrollbar — the trend caption then needed scrolling to
                read, even though nothing was horizontally clipped anymore. Height must
                stay exactly 2 lines per column, always, so growing overflow-hidden's
                (TileContent) box is never in play. Instead the columns share the row's
                width as equal minmax(0,1fr) grid tracks (allowed to shrink) and every text
                node gets truncate (ellipsis) as the last-resort fallback below the
                clamp floor — a shortened line, never an extra one. */}
            <KPIValueRow>
              <KPIValueColumn>
                <KPIValueSlot>
                  <KPIBigValue>{isNumeric ? `${rate.toFixed(1)} %` : '—'}</KPIBigValue>
                </KPIValueSlot>
                {periodLabel && <KPICaption>{periodLabel}</KPICaption>}
              </KPIValueColumn>

              {effectiveComparisonEnabled && (
                <KPIValueColumn>
                  {trend ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <KPITrendSlot className="cursor-default">
                          <KPITrendBadge trend={trend}>{formatTrendPP(trend)}</KPITrendBadge>
                        </KPITrendSlot>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div>Aktuell: {rate.toFixed(1)} %</div>
                        <div>Vorperiode: {previousRate.toFixed(1)} %</div>
                        {previousPeriodLabel && <div>{previousPeriodLabel}</div>}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <KPITrendSlot>
                      <KPICaption>Kein Vergleichswert verfügbar</KPICaption>
                    </KPITrendSlot>
                  )}

                  {trend && comparisonCaption && <KPICaption>{comparisonCaption}</KPICaption>}
                </KPIValueColumn>
              )}
            </KPIValueRow>
          </div>
        </TooltipProvider>

        {showExtraStats && (
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-green-50 rounded-md p-3">
              <div className="text-2xl font-bold text-green-700">{data.on_time_count}</div>
              <div className="text-xs text-gray-500">On Time</div>
            </div>
            <div className="bg-red-50 rounded-md p-3">
              <div className="text-2xl font-bold text-red-700">{data.late_count}</div>
              <div className="text-xs text-gray-500">Late</div>
            </div>
            <div className="bg-gray-50 rounded-md p-3">
              <div className="text-2xl font-bold text-gray-700">{data.total_evaluated}</div>
              <div className="text-xs text-gray-500">Evaluated</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const showInitialButton = !hasFetched && !loading;
  const hasData = !loading && !error && data !== null;

  return (
    <BaseTile loading={loading} error={error} minHeight="sm" padding="sm">
      <TileContent layout="flex" direction="column" grow>
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={handleOpenSettings}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure KPI
            </button>
          </div>
        )}

        {hasData && (
          <div className="pt-2">{renderKPICard()}</div>
        )}

        {hasFetched && !loading && !error && !hasData && (
          <TileStatusContent
            status="empty"
            title="No Data Found"
            message="No objects with evaluable date fields found. Try adjusting your filters or date fields."
            action={
              <button
                onClick={fetchKPIData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors"
              >
                Retry
              </button>
            }
          />
        )}

        {error && hasFetched && (
          <div className="flex justify-center mt-4">
            <button
              onClick={fetchKPIData}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
      </TileContent>

      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        onApply={handleApplySettings}
        title="Delivery Date Compliance KPI — Settings"
        isApplyDisabled={!activeFile || !isFormValid() || loading}
        applyButtonText={loading ? 'Calculating…' : hasFetched ? 'Update KPI' : 'Calculate KPI'}
      >
        <LiefertermintreueKPISettings
          kpiTitle={kpiTitle}
          onKpiTitleChange={setKpiTitle}
          objectType={objectType}
          onObjectTypeChange={handleObjectTypeChange}
          refDateType={refDateType}
          onRefDateTypeChange={handleRefDateTypeChange}
          refDateValue={refDateValue}
          onRefDateValueChange={setRefDateValue}
          actualDateType={actualDateType}
          onActualDateTypeChange={handleActualDateTypeChange}
          actualDateValue={actualDateValue}
          onActualDateValueChange={setActualDateValue}
          tileEventAttributeFilter={tileEventAttributeFilter}
          onTileEventAttributeFilterChange={setTileEventAttributeFilter}
          objectTypes={objectTypes}
          activitiesForType={activitiesForType}
          dateAttrsForType={dateAttrsForType}
          loading={loading}
          activitiesLoading={activitiesLoading}
          dateAttrsLoading={dateAttrsLoading}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
          availableActivities={availableActivities}
          periodPreset={periodPreset}
          onPeriodPresetChange={setPeriodPreset}
          customStart={customStart}
          onCustomStartChange={setCustomStart}
          customEnd={customEnd}
          onCustomEndChange={setCustomEnd}
          showPeriodLabel={showPeriodLabel}
          onShowPeriodLabelChange={setShowPeriodLabel}
          showExtraStats={showExtraStats}
          onShowExtraStatsChange={setShowExtraStats}
          comparisonEnabled={comparisonEnabled}
          onComparisonEnabledChange={setComparisonEnabled}
          trendDirection={trendDirection}
          onTrendDirectionChange={setTrendDirection}
        />
      </TileSettingsModal>
    </BaseTile>
  );
};

export default LiefertermintreueKPI;
